/**
 * useLeadFacts.ts — fetch state for a lead's captured facts (E12-S5.3).
 *
 * Owns `GET /api/leads/{id}/facts` independently of `useLeadDetail` — a facts
 * fetch failure must never break the rest of the lead detail page, so this
 * hook has its own phase and never surfaces `unauthorized` (a 401/403 here
 * would mean the whole page is already unauthorized via useLeadDetail).
 */

import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import type { LeadFact } from '../../api/types';

export type LeadFactsPhase = 'loading' | 'ready' | 'error';

export interface LeadFactsState {
  facts: LeadFact[];
  phase: LeadFactsPhase;
}

export const useLeadFacts = (apiClient: ApiClient, leadId: string): LeadFactsState => {
  const [facts, setFacts] = useState<LeadFact[]>([]);
  const [phase, setPhase] = useState<LeadFactsPhase>('loading');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setPhase('loading');
    apiClient
      .getLeadFacts(leadId)
      .then((payload) => {
        if (!mountedRef.current) return;
        setFacts(payload.facts);
        setPhase('ready');
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setPhase('error');
      });
  }, [apiClient, leadId]);

  return { facts, phase };
};
